import Bugsnag from '@bugsnag/js';
import { isFeatureEnabled, ProjectFeature } from '@civilgrid/utils/src/common/featureAccess';
import { proxy, subscribe } from 'valtio';
import { proxySet } from 'valtio/utils';
import {
  getLatestCivilSearchActivity as apiGetLatestActivity,
  getProject as apiGetProject,
  getDataRequestsV2,
  getProjectAgencies,
  getUtilityAgencies,
  populateProjectAgencies,
} from '~/api/projects2';
import { isFeatureFlagEnabled, posthogFlagsStore } from '~/common/posthog';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { getPref, Prefs, setPref } from '~/common/prefs';
import { type DataRequestV2, RequestType, type Subscription } from '~/common/types';
import rootStore from '~/store';
import type { ProjectWithDetails as Project } from '../details/common';
import { CONNECTBASE_COVERAGE_FLAG, REQUEST_ONLY_AGENCIES_FLAG, type UtilityAgency } from './common';
import { mergeRequestOnlyAgencies, synthesizeRequestOnlyAgencies } from './common/requestOnlyAgencies';
import { DOCUMENT_STEP, stepConfig } from './drawers/RequestData/common';
import { SLOW_LOAD_THRESHOLD_MS } from './loadTelemetry';

type QueryReturn = {
  agencies: UtilityAgency[];
  dataRequests: DataRequestV2[];
};

export type TFormState = {
  step: number;
  files: { [key: string]: FormData };
  filesConfirmed: boolean;
  feesConfirmed: boolean;
  narrativeConfirmed: boolean;
  mapsSelection: Set<string>;
  asbuiltsSelection: Set<string>;
};

export interface CivilSearchNotifications {
  message?: boolean;
  activity?: boolean;
}

const initialFormState: TFormState = {
  step: 0,
  files: {},

  filesConfirmed: false,
  feesConfirmed: false,
  narrativeConfirmed: false,

  mapsSelection: proxySet([]),
  asbuiltsSelection: proxySet([]),
};

class Store {
  isLoading: boolean = true;
  isError: boolean = false;

  project?: Project;

  agencies: { [key: string]: UtilityAgency } = {};
  dataRequests: { [key: string]: DataRequestV2 } = {};
  // CG-1219: rows for request-backed agencies missing from the project agency list, held apart
  // from `agencies` so flag resolution after the fetch can merge them in without a refetch.
  requestOnlyAgencies: { [key: string]: UtilityAgency } = {};

  mapRequestAgencyIds: Set<string> = proxySet([]);
  asbuiltRequestAgencyIds: Set<string> = proxySet([]);

  formState: TFormState = initialFormState;

  notifications: CivilSearchNotifications = {};

  // Mirrored from rootStore.currentOrganization (kept in sync below). Read this — NOT rootStore —
  // from the getter so `useSnapshot(store)` consumers track it: valtio only tracks property reads on
  // the proxy passed to useSnapshot, so a cross-proxy read of rootStore here would go stale.
  subscription?: Subscription;
  // Mirror of the Connectbase-coverage flag so non-hook cell renderers can read it reactively.
  connectbaseCoverageEnabled: boolean = false;
  // Mirror of the request-only-agencies flag (CG-1219), kept in sync below.
  requestOnlyAgenciesEnabled: boolean = false;

  get isFullUtilitiesExperienceEnabled() {
    if (!this.project) return false;
    return isFeatureEnabled(ProjectFeature.CivilSearch, this.project.billingTier, this.subscription);
  }

  // The redesigned Contacts button lives on the Utilities page, which has its own feature gate.
  get isUtilitiesPageEnabled() {
    if (!this.project) return false;
    return isFeatureEnabled(ProjectFeature.Utilities, this.project.billingTier, this.subscription);
  }

  get selectedAgencyIds() {
    const ids = new Set([...Array.from(this.formState.mapsSelection), ...Array.from(this.formState.asbuiltsSelection)]);
    return Array.from(ids);
  }

  get constraints() {
    let hasFileConstraints = false;
    let hasFeeConstraints = false;
    this.selectedAgencyIds.forEach((id) => {
      if ((this.agencies[id].links?.length ?? 0) > 0) {
        hasFileConstraints = true;
      }
      if (this.agencies[id].chargesMapFees) {
        hasFeeConstraints = true;
      }
    });

    let acknowledged = true;
    if (hasFeeConstraints) {
      acknowledged = acknowledged && this.formState.feesConfirmed;
    }
    if (hasFileConstraints) {
      acknowledged = acknowledged && this.formState.filesConfirmed;
    }

    return { hasFileConstraints, hasFeeConstraints, acknowledged };
  }

  async fetchProject(projectId: string) {
    this.isLoading = true;
    const project = await apiGetProject<Project>(projectId);

    this.project = project;
  }

  async populateAgencies(projectId: string) {
    await populateProjectAgencies(projectId);
  }

  async fetchDataLegacy(projectId: string) {
    this.reset();

    try {
      this.isLoading = true;

      const [{ agencies, dataRequests }, project, latestActivity] = await Promise.all([
        await getUtilityAgencies<QueryReturn>(projectId, undefined, true),
        await apiGetProject<Project>(projectId),
        await apiGetLatestActivity(projectId),
      ]);

      this.project = project;

      this.agencies = {};
      this.dataRequests = {};

      agencies?.forEach((pa) => {
        this.agencies[pa.id] = pa;
      });

      dataRequests?.forEach((r) => {
        this.dataRequests[r.id] = r;
        if (r.type === RequestType.MapsRequested) {
          this.mapRequestAgencyIds.add(r.agency.id);
        } else if (r.type === RequestType.AsbuiltsRequested) {
          this.asbuiltRequestAgencyIds.add(r.agency.id);
        }
      });

      const messageId = this.getLastViewed(Prefs.LAST_VIEWED_MESSAGE_ID);
      const activityId = this.getLastViewed(Prefs.LAST_VIEWED_ACTIVITY_ID);

      this.setNotification('message', messageId, latestActivity.message?.id);
      this.setNotification('activity', activityId, latestActivity.activity?.id);
    } catch (e) {
      console.error('Error fetching data: ', e);
      this.isError = true;
    } finally {
      this.isLoading = false;
    }
  }

  async fetchData(projectId: string) {
    this.reset();

    const fetchStart = performance.now();
    // Fires while the fetch is still in flight; cleared in finally on any exit.
    const slowLoadTimer = setTimeout(() => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesTabSlowLoadDetected, {
        project_id: projectId,
        load_phase: 'data_fetch',
        elapsed_ms: SLOW_LOAD_THRESHOLD_MS,
      });
    }, SLOW_LOAD_THRESHOLD_MS);

    try {
      this.isLoading = true;

      const [projectAgencies, dataRequests, project, latestActivity] = await Promise.all([
        await getProjectAgencies(projectId),
        await getDataRequestsV2(projectId),
        await apiGetProject<Project>(projectId),
        await apiGetLatestActivity(projectId),
      ]);

      this.project = project;

      this.agencies = {};
      this.dataRequests = {};

      projectAgencies?.forEach((pa) => {
        this.agencies[pa.id] = pa;
      });

      dataRequests?.forEach((r) => {
        this.dataRequests[r.id] = r;
        if (r.type === RequestType.MapsRequested) {
          this.mapRequestAgencyIds.add(r.agency.id);
        } else if (r.type === RequestType.AsbuiltsRequested) {
          this.asbuiltRequestAgencyIds.add(r.agency.id);
        }
      });

      this.requestOnlyAgencies = synthesizeRequestOnlyAgencies(projectAgencies ?? [], dataRequests ?? []);
      this.syncRequestOnlyAgencies();

      const messageId = this.getLastViewed(Prefs.LAST_VIEWED_MESSAGE_ID);
      const activityId = this.getLastViewed(Prefs.LAST_VIEWED_ACTIVITY_ID);

      this.setNotification('message', messageId, latestActivity.message?.id);
      this.setNotification('activity', activityId, latestActivity.activity?.id);

      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesTabLoaded, {
        project_id: projectId,
        load_phase: 'data_fetch',
        outcome: 'completed',
        duration_ms: Math.round(performance.now() - fetchStart),
      });
    } catch (e) {
      console.error('Error fetching data: ', e);
      this.isError = true;
      captureTypedPosthogEvent(POSTHOG_EVENTS.utilitiesTabLoaded, {
        project_id: projectId,
        load_phase: 'data_fetch',
        outcome: 'failed',
        duration_ms: Math.round(performance.now() - fetchStart),
      });
      Bugsnag.notify(e instanceof Error ? e : new Error(`Utilities tab data fetch failed: ${String(e)}`));
    } finally {
      clearTimeout(slowLoadTimer);
      this.isLoading = false;
    }
  }

  /**
   * CG-1219: merge the request-only rows into `agencies` (or take them back out) to match the
   * flag. Runs after each fetch and again when flags resolve, which can land either side of it.
   */
  syncRequestOnlyAgencies() {
    mergeRequestOnlyAgencies(this.agencies, this.requestOnlyAgencies, this.requestOnlyAgenciesEnabled);
  }

  getInitialFormState(): TFormState {
    return {
      step: 0,
      files: {},

      filesConfirmed: false,
      feesConfirmed: false,
      narrativeConfirmed: false,

      mapsSelection: proxySet(),
      asbuiltsSelection: proxySet(),
    };
  }

  resetAllFormFields() {
    this.formState = this.getInitialFormState();
    this.mapRequestAgencyIds.clear();
    this.asbuiltRequestAgencyIds.clear();
  }

  toggleFormStep(inc: 1 | -1) {
    const prev = this.formState.step;
    let v = prev + inc;

    if (!stepConfig[v]) return;

    const hasConstraints = this.constraints.hasFeeConstraints || this.constraints.hasFileConstraints;

    if (v === DOCUMENT_STEP && !hasConstraints) {
      // if no constraints, then add another increment to the step to skip document step.
      v = v + inc;
    }

    this.formState.step = v;
  }

  getLastViewed(pref: Prefs) {
    if (!this.project) return;
    const val = getPref(pref, this.project.id);
    return val?.split('.')?.[0];
  }

  setLastViewed(pref: Prefs, notification: keyof CivilSearchNotifications, id: string) {
    if (!this.project) return;
    setPref(pref, id, this.project.id);
    this.notifications[notification] = false;
  }

  setNotification(notif: keyof CivilSearchNotifications, local?: string, latest?: string) {
    if (!latest) return;

    if (local) {
      if (local !== latest) {
        this.notifications[notif] = true;
      }
    } else {
      this.notifications[notif] = true;
    }
  }

  reset() {
    this.resetAllFormFields();
    this.isLoading = true;
    this.isError = false;
    this.project = undefined;
    this.agencies = {};
    this.dataRequests = {};
    this.requestOnlyAgencies = {};
    this.notifications = {};
  }
}

const store = proxy(new Store());

// Keep `store.subscription` and the flag mirrors in sync so the getters above stay reactive for every
// `useSnapshot(store)` consumer (utilities tab AND the separate reports tab) without a cross-proxy
// read. Sync from rootStore (org/subscription changes) and posthogFlagsStore (flags resolving for the
// active org).
function syncFeatureProvisioningState() {
  store.subscription = rootStore.currentOrganization?.subscription;
  const resolved =
    posthogFlagsStore.flagsResolvedForOrgId !== null &&
    posthogFlagsStore.flagsResolvedForOrgId === rootStore.currentOrganizationId;
  store.connectbaseCoverageEnabled = resolved && isFeatureFlagEnabled(CONNECTBASE_COVERAGE_FLAG);
  store.requestOnlyAgenciesEnabled = resolved && isFeatureFlagEnabled(REQUEST_ONLY_AGENCIES_FLAG);
  store.syncRequestOnlyAgencies();
}
syncFeatureProvisioningState();
subscribe(rootStore, syncFeatureProvisioningState);
subscribe(posthogFlagsStore, syncFeatureProvisioningState);

export default store;
